<template>
  <div
    class="row"
    :class="{
      active: isPopoverActive,
    }"
  >
    <div v-if="canEdit" class="handle">
      <svg-icon :width="'14'" :height="'14'" icon="app-icons/drag" />
    </div>
    <VDropdown
      v-if="canEdit"
      class="dots"
      v-bind="options"
      popper-class="v-popper--globalVars-dropdown"
      placement="bottom"
      @show="isPopoverActive = true"
      @hide="isPopoverActive = false"
    >
      <button type="button" class="btn-add">
        <svg-icon :width="'14'" :height="'14'" icon="app-icons/dot-menu" />
      </button>
      <template #popper>
        <div class="menu">
          <div v-close-popper class="option" @click="onClickCreate">
            Create new variable
          </div>
          <div v-close-popper class="option" @click="onClickRemove">
            Delete variable
          </div>
        </div>
      </template>
    </VDropdown>
    <input
      ref="name"
      v-model="variable.name"
      class="name"
      placeholder="Enter var name..."
      spellcheck="false"
      :disabled="!canEdit"
      tabindex="0"
      @blur="onBlurName"
    />
    <select
      id="type"
      v-model="variable.type"
      :disabled="!canEdit"
      tabindex="0"
      class="type"
      name="type"
      @change="onChangeType"
    >
      <option v-for="v in VARIABLES" :key="v.type" :value="v.type">
        {{ v.type }}
      </option>
    </select>
    <input
      v-if="variable.type !== 'boolean'"
      ref="initialValue"
      v-model="variable.value"
      tabindex="0"
      :type="inputMode.type"
      :step="inputMode.step"
      class="initialValue"
      placeholder="Enter initial value..."
      spellcheck="false"
      :disabled="!canEdit"
      @blur="onBlurValue"
    />
    <select
      v-else
      id="booleanValue"
      ref="initialValue"
      v-model="variable.value"
      :disabled="!canEdit"
      tabindex="0"
      class="initialValue"
      @change="onChangeBooleanValue"
    >
      <option value="true">true</option>
      <option value="false">false</option>
    </select>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import _ from 'lodash';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, VARIABLES } from '@/constants.js';
import { has } from '../../../utils.js';
import SvgIcon from '../../all/SvgIcon.vue';

export default {
  components: {
    SvgIcon,
  },

  props: {
    // The variable id
    id: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      // The various variable types that the user may select from
      VARIABLES: Object.freeze(VARIABLES),
      tooltipOptions: {
        placement: 'top',
        delay: { show: 800, hide: 0 },
      },
      isPopoverActive: false,
      options: {
        placement: 'bottom-middle',
        triggers: ['click'],
        boundary: document.body,
        delay: {
          show: 100,
          hide: 100,
        },
        distance: '0',
      },
    };
  },

  computed: {
    ...mapGetters('project/data/variables', [
      'getNode',
      'getIndex',
      'getNames',
      'getVariables',
    ]),

    /**
     * Returns the variable object for the given id
     * @returns {Object}
     */
    variable() {
      const variable = _.cloneDeep(this.getNode(this.id)); // Return a copy so that it can be used in v-bind
      if (!variable.name) variable.name = this.defaultName;
      return variable;
    },

    /**
     * Returns the index of this variable
     * @returns {number}
     */
    index() {
      return this.getIndex(this.id, 'variables', 'children');
    },

    /**
     * Returns the type of the input (number or text)
     * @returns {string}
     */
    inputMode() {
      const { type } = this.getNode(this.id);
      if (type === 'integer') {
        return { type: 'number', step: '1' };
      }
      if (type === 'float') {
        return { type: 'number', step: '0.1' };
      }
      return { type: 'text' };
    },

    /**
     * Returns the default name of a variable (variable_1, variable_2 etc)
     * @returns {string}
     */
    defaultName() {
      let index = 0;
      let name = null;
      const names = this.getNames;
      do {
        index += 1;
        name = `variable_${index}`;
      } while (names.includes(name));
      return name;
    },
  },

  methods: {
    ...mapActions('project/data/variables', ['deleteProperty']),
    ...mapActions('project/data/variables', {
      deleteVariable: 'customDelete',
      updateVariable: 'customUpdate',
      createVariable: 'customCreate',
    }),
    ...mapActions('application/notifications', ['createError']),
    ...mapActions('project/data/elements', {
      elementsRenameVariables: 'renameVariables',
    }),
    ...mapActions('project/data/connections', {
      connectionsRenameVariables: 'renameVariables',
    }),
    ...mapActions('project/data/conditions', {
      conditionsRenameVariables: 'renameVariables',
    }),

    /**
     * Event handler: The user clicks on the create variable menu option
     */
    onClickCreate() {
      // Create the new variable at the index of the current variable
      this.createVariable({
        props: { name: this.defaultName },
        tIndex: this.index,
        apply: true,
      });
    },

    /**
     * Event handler: The user clicks on the remove variable menu option
     */
    onClickRemove() {
      this.deleteVariable({ id: this.id, apply: true });
    },

    /**
     * Event handler for blur event on the name field
     */
    onBlurName(event) {
      const { value } = event.target;
      const oldName = this.getNode(this.id).name;
      console.log('old name is:', oldName);

      // Do nothing if the name is unchanged
      if (value === oldName) return;

      // If the new name is empty restore the old one
      if (!value) {
        this.createError({ text: `The variable should have a name` });
        event.target.value = oldName;
        return;
      }

      // If the new name is not a valid variable name restore the old one
      const regex = /^[A-Za-z$_][0-9A-Za-z$_]*/;
      if (!regex.test(value)) {
        this.createError({ text: `The variable name: ${value} is invalid` });
        event.target.value = oldName;
        return;
      }

      // If new name already exists restore the old one
      if (this.getNames.includes(value)) {
        this.createError({
          text: `The variable name: ${value} already exists`,
        });
        event.target.value = oldName;
        return;
      }

      const variableObjects = _.cloneDeep(this.getVariables);
      this.updateVariable({
        id: this.id,
        props: { name: value },
        apply: true,
      }).then(() => {
        if (oldName) {
          // Update the all arcscript in project that contains this variable
          const variableRenames = { [this.id]: value };
          this.elementsRenameVariables({
            props: ['content'],
            variableRenames,
            variableObjects,
            apply: false,
          });
          this.conditionsRenameVariables({
            props: ['script'],
            variableRenames,
            variableObjects,
            apply: false,
          });
          this.connectionsRenameVariables({
            props: ['label'],
            variableRenames,
            variableObjects,
            apply: true,
          });
        }
      });
    },

    /**
     * Event handler for blur event on the value field
     */
    onBlurValue(event) {
      let { value } = event.target;

      // Retrieve the current value
      const { value: current } = this.getNode(this.id);

      // Do nothing if the value is unchanged
      if (value === current) return;

      // Modify the value according to the type
      const { type } = this.getNode(this.id);
      value = this.castValueToSpecificType(value, type);
      this.updateVariable({
        id: this.id,
        props: { value },
        apply: true,
      });

      // Reset the input's value to the new one
      event.target.value = value;
    },

    /**
     * Event handler for selection change in select for boolean value.
     * This select element is shown only when the type is 'boolean'.
     */
    onChangeBooleanValue(event) {
      let { value } = event.target;
      value = this.castValueToSpecificType(value, 'boolean');
      this.updateVariable({
        id: this.id,
        props: { value },
        apply: true,
      });
    },

    /**
     * Event handler for selection change in types option
     */
    onChangeType(event) {
      const { value: type } = event.target;

      // Use this type's default value
      const { value } = Object.values(VARIABLES).find(v => v.type === type);

      // Change the value in the variable value input
      this.variable.value = value;
      this.updateVariable({ id: this.id, props: { type, value }, apply: true });
    },

    /**
     * Returns a value according to the variable type
     * @param   {string}  value
     * @param   {string}  type
     * @returns {mixed}
     */
    castValueToSpecificType(value, type) {
      switch (type) {
        case 'integer': {
          // Attempt to convert the existing string value to an integer
          const result = parseInt(value, 10);
          if (Number.isNaN(result)) return 0;
          return result;
        }

        case 'float': {
          // Attempt to convert the existing string value to a float
          const result = parseFloat(value);
          if (Number.isNaN(result)) return 0.0;
          return result;
        }

        case 'boolean':
          return value === 'true';

        case 'string':
          return String(value);

        default:
          throw new Error('Invalid casting to type:', type);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.row {
  display: flex;
  position: relative;
  align-items: center;
  margin: 0 0 10px 0;
  padding: 0 30px;
  // &.sortable-chosen {
  //   background: #2f383a;
  // }
  // &.ghost {
  //   .handle,
  //   .remove {
  //     display: none !important;
  //   }
  // }
  &:hover,
  &.sortable-chosen {
    .handle,
    .dots {
      display: block;
    }
  }
  .handle {
    cursor: move;
    cursor: -webkit-grab;
    opacity: 0.4;
    display: none;
    position: absolute;
    left: 10px;
    top: 50%;
    margin-top: -7px;
    :deep(svg circle) {
      fill: #bcbcbc;
    }
  }

  .dots {
    cursor: pointer;
    opacity: 0.4;
    display: none;
    position: absolute;
    right: 2px;
    top: 50%;
    margin-top: -12px;
  }

  .dots:hover,
  .handle:hover {
    opacity: 0.8;
  }

  &.active .dots,
  .v-popper--shown {
    display: block;
    opacity: 0.8;
  }

  input,
  select {
    height: 42px;
    border: none;
    color: #bcbcbc;
    background-color: #252929;
    padding: 10px;
    border-radius: 3px;
    word-break: break-word;
    font-size: 14px;
    margin: 0 10px 0 0;
    min-width: 0;
    font-family: 'Open Sans', sans-serif;
    transition: background-color 0.1s;
    &.name {
      // color: #67cdfe;ασδ
      flex: 3;
    }
    &.type {
      flex: 1;
    }
    &.initialValue {
      flex: 2;
    }
    &::placeholder {
      color: rgba(255, 255, 255, 0.3);
    }
    &:last-child {
      margin: 0;
    }
    &:focus {
      background-color: #202323;
    }
    &:disabled {
      background: none;
      border: 1px solid #3c4f52;
      color: #bcbcbc !important;
      cursor: default;
    }
  }
  select {
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: url("data:image/svg+xml;utf8,<svg fill='%23bcbcbc' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>");
    background-position: right 5px top 9px;
    background-repeat: no-repeat;
    option {
      font-family: 'Open Sans', sans-serif;
    }
  }
  .type {
    flex: 1;
  }
}
.btn-add {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  border: none;
  width: 26px;
  height: 26px;
  cursor: pointer;
  border-radius: 4px;
}
</style>

<style lang="scss">
.tooltip {
  z-index: 205;
}

.v-popper__popper.v-popper--globalVars-dropdown.v-popper--theme-dropdown {
  .v-popper__inner {
    background: #0d0e0e;
    border: 1px solid #414c4d;
    border-radius: 3px;
    box-shadow: 0 1px 10px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    padding: 0;
    color: #bcbcbc;
    margin-top: -10px;
  }

  .v-popper__arrow-container {
    display: none;
  }

  .menu {
    .option {
      align-items: center;
      cursor: pointer;
      display: flex;
      padding: 8px 16px;
      -webkit-appearance: none;
      appearance: none;
      background: transparent;
      border: none;
      color: #bcbcbc;
      width: 100%;
      font-size: 13px;

      &:hover {
        background: #363e3f;
      }
    }
  }
}
</style>
